import { Fragment, useState } from "react";
import { ClassNames } from "@emotion/react";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";
import { Route, Router, Switch, useLocation } from "wouter";

import { withAppLayout } from "components/app_layout/AppLayout";
import ContentLibraryTable from "components/content_library/ContentLibraryTable";
import { EllipsisTooltipText } from "lib/core_components/EllipsisTooltip";
import { BID_LIBRARY, useVariableValue } from "lib/featureFlags";

function ContentLibraryWrapper() {
  return (
    <Router base="/content-library">
      <Switch>
        <Route path="/:folderPath/*?">
          {(params) => {
            const wildcard = params["*"] ?? "";
            const folderPath = [params.folderPath, wildcard].filter(Boolean).join("/");
            return <ContentLibrary folderPath={folderPath} />;
          }}
        </Route>
        <Route path="/">
          <ContentLibrary />
        </Route>
      </Switch>
    </Router>
  );
}

type Props = {
  folderPath?: string;
};
export function ContentLibrary({ folderPath }: Props): JSX.Element {
  const [selectedRowGuids, setSelectedRowGuids] = useState<string[]>([]);
  const isBidLibraryEnabled = useVariableValue(BID_LIBRARY, false);
  const [, setLocation] = useLocation();

  if (!isBidLibraryEnabled) {
    return <></>;
  }

  const segments = folderPath?.split("/") ?? [];

  return (
    <Page>
      {segments.length > 0 && (
        <BreadcrumbContainer>
          <Flex alignItems="center" gap={8}>
            <Breadcrumb onClick={() => setLocation("/")}>Content library</Breadcrumb>
            {segments.map((segment, index) => {
              const path = "/" + segments.slice(0, index + 1).join("/");
              const isLast = index === segments.length - 1;

              return (
                <Fragment key={index}>
                  <Text color="sysTextSecondary"> / </Text>
                  {isLast ? (
                    <ClassNames>
                      {({ css }) => (
                        <EllipsisTooltipText
                          fullText={segment}
                          containerClassname={css({
                            display: "-webkit-box",
                            WebkitLineClamp: 1,
                            lineClamp: 1,
                            WebkitBoxOrient: "vertical",
                            maxWidth: 250,
                          })}
                        />
                      )}
                    </ClassNames>
                  ) : (
                    <Breadcrumb onClick={() => setLocation(path)}>{segment}</Breadcrumb>
                  )}
                </Fragment>
              );
            })}
          </Flex>
        </BreadcrumbContainer>
      )}
      <Content>
        <Flex column gap={24}>
          <Flex justifyContent="space-between" alignItems="center">
            <Text h1>{segments[segments.length - 1] ?? "Content library"}</Text>
          </Flex>
          <ContentLibraryTable
            folderPath={folderPath}
            selectedRowGuids={selectedRowGuids}
            setSelectedRowGuids={setSelectedRowGuids}
          />
        </Flex>
      </Content>
    </Page>
  );
}

const Page = styled(Flex)(({ theme }) => ({
  flexDirection: "column",
  gap: 40,
  overflow: "auto",
  height: "100%",
  width: "100%",
  backgroundColor: theme.colors.sysBackgroundDefault,
}));

const Content = styled(Flex)({
  flexDirection: "column",
  gap: 40,
  padding: 24,
});

const BreadcrumbContainer = styled(Flex)(({ theme }) => ({
  justifyContent: "space-between",
  padding: 24,
  paddingBottom: 0,
  alignItems: "center",
  gap: 8,
  borderBottom: `1px solid ${theme.colors.sysBorderDecorative}`,
}));

const Breadcrumb = styled(Flex)<{ inactive?: boolean }>(({ theme, inactive }) => ({
  alignItems: "center",
  gap: "4px",
  cursor: inactive ? "default" : "pointer",
  color: inactive ? theme.colors.sysTextSecondary : theme.colors.sysPrimaryDefault,
  "&:hover": {
    textDecoration: inactive ? "none" : "underline",
  },
}));

export default withAppLayout(ContentLibraryWrapper, {});
